import _ from 'lodash';
import { type JSX, useMemo, useState } from 'react';
import { generatePath } from 'react-router';

import { DocumentApi } from 'Api/Document/DocumentApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { UcrmAssessmentApi } from 'Api/UCRM/UcrmAssessmentApi';
import { Button } from 'Components/Buttons/Buttons';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { Role } from 'Components/Context/RBACContext';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageDataLoader } from 'Components/PageDataLoader/PageDataLoader';
import { SidebarSection as PageSidebarSection } from 'Components/PageLayout/PageLayoutQuestionnaire/PageLayoutQuestionnaire';
import { RequiresAttentionMarker } from 'Components/RequiresAttentionMarker/RequiresAttentionMarker';
import { Text } from 'Components/Text/Text';
import { DiscussionThread } from 'Components/UcrmTprm/DiscussionThread/DiscussionThread';
import { QuestionnaireForm } from 'Components/UcrmTprm/QuestionnaireForm/QuestionnaireForm';
import { UCRM_ASSESSMENT, UCRM_BUSINESSES_AND_USE_CASES_LISTING, UCRM_QUESTIONNAIRE, UCRM_USE_CASE_DASHBOARD } from 'Config/PathTemplates';
import { AuthHandler } from 'Helpers/Auth/AuthHandler';
import { iso8601ToUsDateShort, jsDateToIso8601 } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { UserNameFormat, getUserNameFromSubject } from 'Helpers/UserUtils';
import { DueDiligenceAnswer } from 'Models/DueDiligence';
import { ProductModule } from 'Models/ProductModule';
import { UcrmDueDiligenceAssessment, UseCase } from 'Models/Ucrm';

export interface AssessmentQuestionnaireLoaderProps {
    assessmentApi: UcrmAssessmentApi;
    authHandler: AuthHandler;
    documentApi: DocumentApi;
    ucrmApi: ClientUcrmApi;
}

export const AssessmentQuestionnaireLoader = (props: AssessmentQuestionnaireLoaderProps) => {
    return <PageDataLoader path={UCRM_QUESTIONNAIRE} fetchData={(urlParams) => Promise.all([props.ucrmApi.getUseCaseDetails(urlParams.third_party_id, urlParams.service_id), props.assessmentApi.getAssessment(urlParams.third_party_id, urlParams.service_id, urlParams.assessment_id)])} renderForm={(result) => <AssessmentQuestionnaire assessmentApi={props.assessmentApi} authHandler={props.authHandler} documentApi={props.documentApi} initialAssessment={result[1].data} useCase={result[0].data} />} />;
};

export interface AssessmentQuestionnaireProps {
    assessmentApi: UcrmAssessmentApi;
    authHandler: AuthHandler;
    documentApi: DocumentApi;
    initialAssessment: UcrmDueDiligenceAssessment;
    useCase: UseCase;
}

/**
 * Renders the questionnaire of a UCRM due diligence assessment for a SummIT user (one of the assessment's questionnaire respondents) to complete.
 * Reached via the Due Diligence Assessments section of the Use Case Dashboard.
 * This is the UCRM counterpart to the TPRM `ThirdPartyQuestionnaire`, where third-party users answer the questionnaire; answering questions is handled by `QuestionnaireForm` in both cases.
 * Users who are not respondents (or admins) can view the questionnaire, but cannot answer or submit it.
 */
const AssessmentQuestionnaire = (props: AssessmentQuestionnaireProps): JSX.Element => {
    const cachedData = useCachedData();
    const userId = useMemo(() => props.authHandler.getAuthenticatedUserSubject()!, [props.authHandler]);
    const userIsAdmin = useMemo(() => props.authHandler.getUserRoles().includes(Role.ADMIN), [props.authHandler]);
    const userCanRespond = userIsAdmin || props.initialAssessment.questionnaire_respondent_ids.includes(userId);

    /** Owned here rather than by `DiscussionThread` so that a partially written message survives the user switching to a different section of the questionnaire. */
    const [draftDiscussionMessage, setDraftDiscussionMessage] = useState('');

    const saveControlGroup = async (assessment: UcrmDueDiligenceAssessment, frameworkId: string, groupId: string, answers: DueDiligenceAnswer[]): Promise<UcrmDueDiligenceAssessment> => {
        return (await props.assessmentApi.saveQuestionnaireControlGroup(assessment.third_party_id, assessment.service_id, assessment.id, frameworkId, groupId, { answers })).data;
    };

    const submitQuestionnaire = async (assessment: UcrmDueDiligenceAssessment): Promise<UcrmDueDiligenceAssessment> => {
        return (await props.assessmentApi.submitQuestionnaire(assessment.third_party_id, assessment.service_id, assessment.id)).data;
    };

    const getLeadingSidebarSections = (assessment: UcrmDueDiligenceAssessment, onAssessmentChange: (updatedAssessment: UcrmDueDiligenceAssessment) => void): PageSidebarSection[] => {
        const lastReadTimestampForUser: string | undefined = assessment.discussion.marked_as_read_timestamps_client[userId];
        const unreadMessageCount = lastReadTimestampForUser ? assessment.discussion.messages.filter((message) => message.timestamp > lastReadTimestampForUser).length : assessment.discussion.messages.length;

        /** Marks a discussion as read in the background. For immediate feedback, sets the "last read" timestamp in state to a time in the far future; the real timestamp will be loaded next time this page is loaded from scratch. */
        const markMessagesAsRead = () => {
            const updatedAssessment = _.cloneDeep(assessment);
            updatedAssessment.discussion.marked_as_read_timestamps_client[userId] = jsDateToIso8601(new Date(9999, 0));
            onAssessmentChange(updatedAssessment);

            props.assessmentApi.markDiscussionAsRead(assessment.third_party_id, assessment.service_id, assessment.id).catch((error) => {
                console.error('Failed to mark discussion as read:', error);
            });
        };

        const respondentNames = assessment.questionnaire_respondent_ids.map((respondentId) => getUserNameFromSubject(respondentId, cachedData.users, UserNameFormat.FIRST_SPACE_LAST)).filter((name) => name !== undefined);

        return [
            {
                title: 'Status and Discussion',
                content: [
                    {
                        title: 'Status',
                        subtitle: assessment.questionnaire_submitted_timestamp ? `The questionnaire was submitted${assessment.questionnaire_submitted_by ? ` by ${getUserNameFromSubject(assessment.questionnaire_submitted_by, cachedData.users, UserNameFormat.FIRST_SPACE_LAST)}` : ''} on ${iso8601ToUsDateShort(assessment.questionnaire_submitted_timestamp)}.` : 'The questionnaire is in progress.',
                        content: (() => {
                            if (assessment.questionnaire_submitted_timestamp) {
                                return <Text>Answers cannot be updated, but discussion messages can still be sent. To make further changes, the assessment owner or a delegate can reopen the questionnaire when sending a discussion message.</Text>;
                            } else if (userCanRespond) {
                                return (
                                    <Text>
                                        Answer the questions in each section using the sidebar on the right side of the page. When the questionnaire is completed, submit it via the <i>Submit</i> section.
                                    </Text>
                                );
                            } else {
                                return <Text>Only the questionnaire's respondents can answer and submit it.</Text>;
                            }
                        })(),
                    },
                    {
                        title: 'Respondents',
                        content: <Text>{respondentNames.length > 0 ? respondentNames.join(', ') : '-'}</Text>,
                    },
                    {
                        title: 'Discussion',
                        content: (
                            <>
                                <Text>Messages may be sent between the questionnaire's respondents and the assessment's owner and delegates.</Text>
                                {unreadMessageCount > 0 && (
                                    <>
                                        <Text variant="Text3" color="darkGray" noStyles>
                                            Messages were added since you last viewed the discussion.
                                        </Text>
                                        <Button variant="linkText" onClick={markMessagesAsRead} size="sm">
                                            Mark as Read
                                        </Button>
                                    </>
                                )}
                                <DiscussionThread perspective="assessment" assessment={assessment} assessmentApi={props.assessmentApi} discussion={assessment.discussion} module={ProductModule.UCRM} onMessageSent={onAssessmentChange} clientName={cachedData.clientDetails.client_name} organizationName={props.useCase.vendor_name} users={cachedData.users} draftMessage={draftDiscussionMessage} hideReopenQuestionnaireOption onDraftMessageChange={setDraftDiscussionMessage} />
                            </>
                        ),
                    },
                ],
                additionalContent:
                    unreadMessageCount > 0 ? (
                        <RequiresAttentionMarker type="warning" size="sm">
                            {`${unreadMessageCount} unread message${unreadMessageCount === 1 ? '' : 's'}`}
                        </RequiresAttentionMarker>
                    ) : undefined,
            },
        ];
    };

    return (
        <QuestionnaireForm<UcrmDueDiligenceAssessment>
            cannotRespondReason={userCanRespond ? undefined : "Only the questionnaire's respondents can answer and submit it."}
            documentApi={props.documentApi}
            getLeadingSidebarSections={getLeadingSidebarSections}
            headerBreadcrumb={
                <Breadcrumb>
                    <BreadcrumbLink link={generatePath(UCRM_BUSINESSES_AND_USE_CASES_LISTING)}>{ProductModule.UCRM}</BreadcrumbLink>
                    <BreadcrumbLink link={generatePath(UCRM_USE_CASE_DASHBOARD, { third_party_id: props.initialAssessment.third_party_id, service_id: props.initialAssessment.service_id })}>{`${props.useCase.vendor_name} - ${props.useCase.name}`}</BreadcrumbLink>
                    <BreadcrumbLink link={generatePath(UCRM_ASSESSMENT, { third_party_id: props.initialAssessment.third_party_id, service_id: props.initialAssessment.service_id, assessment_id: props.initialAssessment.id })}>{props.initialAssessment.name}</BreadcrumbLink>
                    <BreadcrumbText>Questionnaire</BreadcrumbText>
                </Breadcrumb>
            }
            headerDescription={`Complete this questionnaire for ${props.useCase.vendor_name} - ${props.useCase.name}${props.initialAssessment.questionnaire_due_date ? ` before ${iso8601ToUsDateShort(props.initialAssessment.questionnaire_due_date)}.` : '.'}`}
            headerTitle={`${props.initialAssessment.name} Questionnaire`}
            initialQuestionnaire={props.initialAssessment}
            isSubmitted={(assessment) => !!assessment.questionnaire_submitted_timestamp}
            notifiedParty="the assessment owner and delegates"
            saveControlGroup={saveControlGroup}
            submitQuestionnaire={submitQuestionnaire}
        />
    );
};
